import { Injectable, NgZone, inject } from '@angular/core';
import { PoNotificationService } from '@po-ui/ng-components';

import { LoginService } from './login.service';

/**
 * Auto-logout por inatividade (decisao de seguranca 2026-06-01).
 *
 * Quando o usuario fica X minutos sem interagir (mouse/teclado/toque/scroll),
 * a sessao e encerrada e ele e redirecionado pro login. Reduz a janela em
 * que uma sessao aberta e abandonada pode ser usada por terceiros.
 *
 * Combina com o sessionStorage (sessao por aba, some ao fechar o navegador).
 * O watcher roda fora do Angular zone (eventos passivos) pra nao disparar
 * change detection a cada movimento de mouse; so reentra na zone no logout.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {

  /** Tempo de inatividade ate o logout automatico (15 minutos). */
  private static readonly TIMEOUT_MS = 15 * 60 * 1000;

  /** Eventos que contam como "atividade" do usuario. */
  private static readonly ACTIVITY_EVENTS = [
    'mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'
  ];

  private readonly zone = inject(NgZone);
  private readonly login = inject(LoginService);
  private readonly notif = inject(PoNotificationService);

  private timer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private readonly onActivity = () => this.reset();

  /** Inicia o watcher. Idempotente. */
  start(): void {
    if (this.started) { return; }
    this.started = true;
    this.zone.runOutsideAngular(() => {
      IdleTimeoutService.ACTIVITY_EVENTS.forEach(ev =>
        document.addEventListener(ev, this.onActivity, { passive: true })
      );
    });
    this.reset();
  }

  /** Reinicia o contador de inatividade. */
  private reset(): void {
    if (this.timer) { clearTimeout(this.timer); }
    this.timer = setTimeout(() => this.expire(), IdleTimeoutService.TIMEOUT_MS);
  }

  /** Dispara o logout quando o tempo de inatividade estoura. */
  private expire(): void {
    // So desloga se houver sessao ativa - evita redirect/ruido na tela de
    // login. Reentra na zone do Angular pq mexe em router/notification.
    if (!this.login.isLogged()) {
      this.reset();
      return;
    }
    this.zone.run(() => {
      this.login.logout();
      this.notif.warning('Sessao encerrada por inatividade. Faca login novamente.');
    });
  }
}
